import Header from "common/components/Header/Header";
import FilteredList from "common/components/Table/FilteredList";
import useRouter from "common/hooks/use-router";
import useAuth from "common/hooks/useAuth";

import useItem from "common/hooks/useItem";
import useList from "common/hooks/useList";
import React, {
  // eslint-disable-next-line comma-dangle
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";

import { Superfamily } from "types/Superfamily";
import AlertsContext from "common/providers/alerts";
import { GiCheckboxTree } from "react-icons/gi";
import usePark from "common/hooks/usePark";
import GenusSelect from "components/select/GenusSelect/GenusSelect";
import TagSelect from "components/select/TagSelect/TagSelect";
import SuperfamilyItem from "./SuperfamilyItem";

function Superfamilies() {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { setAlert } = useContext(AlertsContext);

  const { park } = usePark();

  const { user: currentUser } = useAuth();
  const canManageSuperfamily =
    (currentUser?.isSuperadmin ||
      currentUser?.isAdmin ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        currentUser?.administrationPermissions?.superfamily?.create)) ??
    false;

  const {
    fetchItems,
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    removeListItem,
    itemsCount,
    itemsPerPage,
    error,
  } = useList<Superfamily>("superfamilies", {
    defaultFilters:
      currentUser?.isSuperadmin && park?.id
        ? {
            park: park.id,
          }
        : {},
    defaultSort: "name:ASC",
    cachePrefix: "Superfamily_list_all",
  });
  const { removeItem } = useItem<Superfamily>("superfamilies", "");

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  const handleDelete = useCallback(
    async (id: any) => {
      try {
        removeListItem(id);
        await removeItem(id);
        fetchItems();
      } catch (e) {
        setAlert(e, "danger");
        return e;
      }
      return true;
    },
    [removeListItem, fetchItems, removeItem, setAlert]
  );

  const handleNavigate = (id: any) => {
    navigate(`/superfamilies/edit/${id}`);
  };

  const columns = useMemo(
    () => [
      {
        Header: t("forms.name"),
        filterName: "name$containsi",
        filterLabel: t("filters.search"),
        columnClasses: "w-1/2",
      },
      {
        Header: t("forms.genus"),
        filterName: "genera",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <GenusSelect isFilter allowReadOnlyOptions {...props} />
        ),
      },
      {
        Header: t("forms.tag"),
        filterName: "tags",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <TagSelect isFilter allowReadOnlyOptions {...props} />
        ),
      },
    ],
    [t]
  );

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500">
      <Header
        title={t("titlePages.superfamilies")}
        subtitle={t("titleLegends.superfamily")}
        btnTitle={t("newItem.superfamily")}
        btnSubtitle={t("newItem.superfamilyLegend")}
        Icon={GiCheckboxTree}
        addLink="/superfamilies/add"
        addLinkDisabled={!canManageSuperfamily}
        isBeforeFilters
      />

      <FilteredList
        data={items ?? []}
        columns={columns}
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={SuperfamilyItem}
        onDelete={handleDelete}
        onNavigate={handleNavigate}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default Superfamilies;
